import React, { useMemo } from 'react';
import Autolinker from 'autolinker';
import { Link } from '.';
import { mentionRegex } from '../../common/use-plaintext-mentions';

type ParsedMentionElement = React.ReactElement | string;

interface MentionProps {
  text: string;
}

const linker = new Autolinker({
  className: 'content__title-link',
  phone: false,
  mention: false,
  hashtag: false,
  sanitizeHtml: true,
});

const parseMentions = (text: string): ParsedMentionElement[] => {
  const chunks: ParsedMentionElement[] = [];
  let lastIndex = 0;

  text.replace(mentionRegex, (match, userId, userName, index) => {
    // Add the text from the last index to the start of the mention as HTML
    const rawText = text.slice(lastIndex, index);
    if (rawText) {
      chunks.push(rawText);
    }
    // Add the Link component for the mention
    chunks.push(
      <Link
        key={userId}
        href={`/profiles/${userId}`}
        action="push"
        className="mention"
      >
        @{userName}
      </Link>
    );

    // Update the last index to be the end of the mention
    lastIndex = index + match.length;
    return match; // This is required for the replace function, but isn't used
  });

  // Add the text after the last mention as HTML
  if (lastIndex < text.length) {
    chunks.push(text.slice(lastIndex));
  }

  return chunks;
};

// FE-32710: HTML-decode entity-encoded input before passing to Autolinker. Autolinker is
// configured with `sanitizeHtml: true`, which re-escapes any `&` in its input — so if the
// backend ever emits `&lt;` (which pony historically did, see pony#6570), the result here
// would be `&amp;lt;` and the browser would render the literal entity name. Decoding first
// makes this component resilient to both raw and entity-encoded inputs.
function decodeHtmlEntities(text: string): string {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
}

//DOES NOT HANDLE SANITIZING OF THE TEXT
//Sanitization is performed by Autolinker (`sanitizeHtml: true` in `linker` above), which
//escapes any HTML in `chunk` before linkification. The decode step above is XSS-safe because
//its output is fed back through that sanitizer before reaching `dangerouslySetInnerHTML`.
export const DangerousHTMLWithMentionsAndAutolinks: React.FC<MentionProps> = ({
  text,
}) => {
  const parsedMentions = useMemo(() => {
    const decoded = decodeHtmlEntities(text);
    return parseMentions(decoded).map((chunk, index) =>
      typeof chunk === 'string' ? (
        <span
          key={index}
          dangerouslySetInnerHTML={{ __html: linker.link(chunk) }}
        />
      ) : (
        chunk
      )
    );
  }, [text]);

  return <>{parsedMentions}</>;
};

//does handles santitzing of the text
export const TextWithMentions: React.FC<MentionProps> = ({ text }) => {
  const parsedMentions = useMemo(() => {
    return parseMentions(text).map((chunk, index) =>
      typeof chunk === 'string' ? <span key={index}>{chunk}</span> : chunk
    );
  }, [text]);

  return <>{parsedMentions}</>;
};
